// Icon upload — POST image bytes to the navigate.st content-addressed store.
//
// The gateway (Argus public-gateway /api/icon-upload) writes the bytes to
// s3://bns/potidaea/icons/<sha256>.<ext> — the key IS the SHA-256 hash, so
// re-uploads of the same content overwrite themselves harmlessly and a
// rogue caller can only pollute their own hash-space.
//
// Client → server contract:
//   POST /api/icon-upload
//   Content-Type: image/(svg+xml|png|jpeg|gif|webp|x-icon)
//   body: raw image bytes (≤ 128 KB)
//   →  { sha256, ext, path: "s3://…", url: "https://…", bytes }
//
// On success we hand back the pieces publishViaLocalSigner needs for the
// DVMB icon sidecar: iconSha256Hex + iconPath (the URL — since chunk 6
// of DVMB is a full URL, we skip the s3://… form entirely).

const UPLOAD_ENDPOINT = 'https://navigate.st/api/icon-upload';

const ALLOWED: ReadonlyArray<string> = [
  'image/svg+xml', 'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/x-icon',
];

const MAX_BYTES = 128 * 1024;

export class IconUploadError extends Error {
  constructor(message: string, readonly code:
    | 'unsupported-type' | 'too-large' | 'server-refused' | 'network-error' | 'offline',
  ) {
    super(message);
    this.name = 'IconUploadError';
  }
}

export interface IconUploadResult {
  sha256Hex: string;    // 64 hex chars — goes into DVMB chunk 5
  url: string;          // full URL — goes into DVMB chunk 6
  ext: string;          // "svg" / "png" / …
  bytes: number;
}

/** POST icon bytes to the shared upload gateway. */
export async function uploadIcon(bytes: Uint8Array, contentType: string): Promise<IconUploadResult> {
  const ct = contentType.split(';')[0].trim().toLowerCase();
  if (!ALLOWED.includes(ct)) {
    throw new IconUploadError('unsupported content-type: ' + ct + ' (allowed: ' + ALLOWED.join(', ') + ')', 'unsupported-type');
  }
  if (bytes.byteLength > MAX_BYTES) {
    throw new IconUploadError('image too large: ' + bytes.byteLength + ' > ' + MAX_BYTES + ' bytes', 'too-large');
  }
  let res: Response;
  try {
    res = await fetch(UPLOAD_ENDPOINT, {
      method: 'POST',
      body: bytes,
      headers: { 'content-type': ct },
    });
  } catch (e) {
    throw new IconUploadError('upload endpoint unreachable: ' + (e instanceof Error ? e.message : String(e)), 'network-error');
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new IconUploadError('upload refused (' + res.status + '): ' + detail.slice(0, 200), 'server-refused');
  }
  const body = await res.json() as { sha256: string; ext: string; url: string; bytes: number };
  if (typeof body.sha256 !== 'string' || body.sha256.length !== 64) {
    throw new IconUploadError('upload gateway returned malformed response', 'server-refused');
  }
  return { sha256Hex: body.sha256, url: body.url, ext: body.ext, bytes: body.bytes };
}
